import { isAxiosError } from "axios";

import { apiClient } from "@/services/apiClient";
import {
  isTotpRequiredResponse,
  requiresTotpEnrollment,
  type ProfessionalTotpRequiredResponse,
} from "@/services/totp-contract";
import {
  clearAuth,
  getToken,
  setAccessExpiresAt,
  setRefreshToken,
  setStoredUser,
  setToken,
} from "@/services/authStorage";

export interface RequestLoginLinkParams {
  email: string;
  locale?: string;
  returnTo?: string;
}

export function requestLoginLink(params: RequestLoginLinkParams): Promise<void> {
  return apiClient
    .post("/auth/login-link", {
      email: params.email.trim().toLowerCase(),
      locale: params.locale,
      returnTo: params.returnTo,
    })
    .then(() => undefined);
}

export interface VerifyResponse {
  accessToken: string;
  sessionId: string;
  expiresAt: string;
  accessTokenExpiresAt?: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
  user: {
    id: string;
    email: string;
    displayName: string | null;
    firstName?: string | null;
    lastName?: string | null;
    profileImage?: string | null;
    tenantId: string | null;
    tenantName: string | null;
    language: string;
    industry: string | null;
    profession: string | null;
    emailVerifiedAt?: string | null;
    platformRole?: "root" | "root_admin" | null;
    isNotary?: boolean;
    isAccountant?: boolean;
    notaryActivationStatus?: "pending" | "active" | null;
    accountantActivationStatus?: "pending" | "active" | null;
    phone?: string | null;
  };
}

export type PlatformLoginOtpRequiredResponse = {
  step: "platform_otp_required";
  pendingLoginId: string;
  expiresAt: string;
};

export type LoginWithPasswordResponse =
  | VerifyResponse
  | PlatformLoginOtpRequiredResponse
  | ProfessionalTotpRequiredResponse;

export function isPlatformOtpRequiredResponse(
  r: LoginWithPasswordResponse
): r is PlatformLoginOtpRequiredResponse {
  return (
    typeof r === "object" &&
    r != null &&
    "step" in r &&
    (r as PlatformLoginOtpRequiredResponse).step === "platform_otp_required"
  );
}

export function loginWithPassword(params: {
  email: string;
  password: string;
  locale?: string;
  adminLogin?: boolean;
}): Promise<LoginWithPasswordResponse> {
  return apiClient
    .post<LoginWithPasswordResponse>("/auth/login", {
      email: params.email.trim().toLowerCase(),
      password: params.password,
      ...(params.locale != null && params.locale !== "" && { locale: params.locale }),
      ...(params.adminLogin === true && { adminLogin: true }),
    })
    .then((res) => res.data);
}

export function completePlatformLoginOtp(params: {
  pendingLoginId: string;
  code: string;
}): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/login/platform-otp", {
      pendingLoginId: params.pendingLoginId,
      code: params.code.replace(/\s/g, ""),
    })
    .then((res) => res.data);
}

export function completeProfessionalTotpLogin(params: {
  pendingLoginId: string;
  code: string;
}): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/login/totp", {
      pendingLoginId: params.pendingLoginId,
      code: params.code.replace(/\s/g, ""),
    })
    .then((res) => res.data);
}

/**
 * Completes a login with a one-time recovery code.
 *
 * Not normalized here beyond trimming: the server accepts the code typed
 * loosely (any case, with or without the dash) because it is read off paper by
 * someone who has just lost their phone.
 */
export function completeProfessionalTotpRecoveryLogin(params: {
  pendingLoginId: string;
  recoveryCode: string;
}): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/login/totp/recovery", {
      pendingLoginId: params.pendingLoginId,
      recoveryCode: params.recoveryCode.trim(),
    })
    .then((res) => res.data);
}

export function resendPlatformLoginOtp(params: {
  pendingLoginId: string;
  locale?: string;
}): Promise<void> {
  return apiClient
    .post("/auth/login/platform-otp/resend", {
      pendingLoginId: params.pendingLoginId,
      ...(params.locale != null && params.locale !== "" && { locale: params.locale }),
    })
    .then(() => undefined);
}

export type CompanyTypeValue =
  | "unipersonal"
  | "sas"
  | "srl"
  | "sa"
  | "monotributo";

export interface ValidateRutResponse {
  legalName: string;
  tradeName?: string;
  legalAddress?: string;
  city?: string;
  department?: string;
  postalCode?: string;
  phone?: string;
  email?: string;
  fechaInicio?: string;
  economicActivity?: string;
  suggestedCompanyType?: CompanyTypeValue | null;
}

export function validateRut(rut: string): Promise<ValidateRutResponse> {
  const normalized = rut.replace(/\D/g, "").padStart(12, "0").slice(0, 12);
  return apiClient
    .post<ValidateRutResponse>("/auth/validate-rut", { rut: normalized })
    .then((res) => res.data);
}

/** True when the registry RUT lookup returned 503 (unavailable), not invalid RUT (400). */
export function isValidateRutServiceUnavailableError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 503;
}

export function sendVerificationEmail(
  email: string,
  locale?: string
): Promise<void> {
  return apiClient
    .post("/auth/send-verification-email", {
      email: email.trim().toLowerCase(),
      ...(locale != null && locale !== "" && { locale }),
    })
    .then(() => undefined);
}

export function verifyEmail(
  email: string,
  code: string
): Promise<{ emailVerified: true; registrationToken: string }> {
  return apiClient
    .post<{ emailVerified: true; registrationToken: string }>(
      "/auth/verify-email",
      { email: email.trim().toLowerCase(), code: code.trim() }
    )
    .then((res) => res.data);
}

export interface RegisterCompanyRegistrationPayload {
  rut: string;
  dgiValidated: boolean;
  legalName?: string;
  legalAddress?: string;
  city?: string;
  department?: string;
  phone?: string;
  contactEmail?: string;
}

export interface RegisterParams {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  registrationToken: string;
  /** Sent when Cloudflare Turnstile is enabled (production + TURNSTILE_SECRET_KEY on the API). */
  captchaToken?: string;
  accountKind?: ConsumerAccountKind;
  companyRegistration?: RegisterCompanyRegistrationPayload;
}

export function register(params: RegisterParams): Promise<VerifyResponse> {
  const body: Record<string, unknown> = {
    firstName: params.firstName.trim(),
    lastName: params.lastName.trim(),
    email: params.email.trim().toLowerCase(),
    password: params.password,
    registrationToken: params.registrationToken,
    ...(params.captchaToken != null &&
      params.captchaToken !== "" && { captchaToken: params.captchaToken }),
    ...(params.accountKind != null && { accountKind: params.accountKind }),
    ...(params.companyRegistration != null && {
      companyRegistration: {
        ...params.companyRegistration,
        rut: params.companyRegistration.rut.replace(/\D/g, "").padStart(12, "0").slice(0, 12),
      },
    }),
  };
  return apiClient.post<VerifyResponse>("/auth/register", body).then((res) => res.data);
}

export interface CreateCompanyParams {
  rut: string;
  legalName?: string;
  companyType?: CompanyTypeValue;
  address?: string;
  city?: string;
  department?: string;
  phone?: string;
  contactEmail?: string;
  industry?: string;
  profession?: string;
}

export function createCompany(params: CreateCompanyParams): Promise<VerifyResponse> {
  const normalizedRut = params.rut.replace(/\D/g, "").padStart(12, "0").slice(0, 12);
  const body: Record<string, unknown> = {
    rut: normalizedRut,
  };
  if (params.legalName != null && params.legalName.trim() !== "") body.legalName = params.legalName.trim();
  if (params.companyType != null) body.companyType = params.companyType;
  if (params.address != null && params.address.trim() !== "") body.address = params.address.trim();
  if (params.city != null && params.city.trim() !== "") body.city = params.city.trim();
  if (params.department != null && params.department.trim() !== "") body.department = params.department.trim();
  if (params.phone != null && params.phone.trim() !== "") body.phone = params.phone.trim();
  if (params.contactEmail != null && params.contactEmail.trim() !== "") body.contactEmail = params.contactEmail.trim();
  if (params.industry != null && params.industry.trim() !== "") body.industry = params.industry.trim();
  if (params.profession != null && params.profession.trim() !== "") body.profession = params.profession.trim();
  return apiClient.post<VerifyResponse>("/auth/create-company", body).then((res) => res.data);
}

export interface SetPasswordParams {
  currentPassword?: string;
  newPassword: string;
}

export function deleteMyAccount(params: {
  confirmEmail: string;
  currentPassword?: string;
}): Promise<void> {
  return apiClient
    .post("/auth/me/delete-account", {
      confirmEmail: params.confirmEmail.trim().toLowerCase(),
      ...(params.currentPassword?.trim()
        ? { currentPassword: params.currentPassword }
        : {}),
    })
    .then(() => undefined);
}

export function setPassword(params: SetPasswordParams): Promise<void> {
  return apiClient
    .patch("/auth/me/password", {
      currentPassword: params.currentPassword,
      newPassword: params.newPassword,
    })
    .then(() => undefined);
}

export function verify(params: {
  token: string;
  activeTenantId?: string;
}): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/verify", {
      token: params.token,
      activeTenantId: params.activeTenantId,
    })
    .then((res) => res.data);
}

/** Platform admin invite from email: `POST /auth/verify-root-invite` (session has no tenant). */
export function verifyRootInvite(params: { token: string }): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/verify-root-invite", {
      token: params.token.trim(),
    })
    .then((res) => res.data);
}

export function exchangeSsoCode(code: string): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/sso/exchange", { code })
    .then((res) => res.data);
}

export interface SetConsumerAccountPayload {
  accountKind: "person" | "company";
  companyRegistration?: RegisterCompanyRegistrationPayload;
}

export function setConsumerAccount(
  payload: SetConsumerAccountPayload,
): Promise<{ ok: true }> {
  return apiClient
    .patch<{ ok: true }>("/auth/me/consumer-account", payload)
    .then((res) => res.data);
}

export function requestPasswordReset(
  email: string,
  locale?: string
): Promise<void> {
  return apiClient
    .post("/auth/forgot-password", {
      email: email.trim().toLowerCase(),
      locale,
    })
    .then(() => undefined);
}

export function resetPasswordWithToken(
  token: string,
  newPassword: string
): Promise<void> {
  return apiClient
    .post("/auth/reset-password", { token, newPassword })
    .then(() => undefined);
}

/** Mirrors backend B2C company snapshot at registration. */
export type ConsumerCompanyRegistrationSnapshot = {
  rut: string;
  legalName: string;
  legalAddress?: string;
  city?: string;
  department?: string;
  phone?: string;
  contactEmail?: string;
  dgiValidated: boolean;
};

export type ConsumerAccountKind = "person" | "company";

export interface SessionResponse {
  userId: string;
  activeTenantId: string | null;
  role: string;
  sessionId: string;
  tenantName: string | null;
  tenantProfileImage: string | null;
  language: string;
  industry: string | null;
  profession: string | null;
  platformRole: "root" | "root_admin" | null;
  isNotary?: boolean;
  isAccountant?: boolean;
  notaryActivationStatus?: "pending" | "active" | null;
  accountantActivationStatus?: "pending" | "active" | null;
  onboardingCompletedAt: string | null;
  onboardingMode: "self" | "accountant" | null;
  hasDgiCredentials: boolean;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  secondName: string | null;
  secondLastName: string | null;
  documentType: "national_id" | "passport" | null;
  documentNumber: string | null;
  identityVerifiedAt: string | null;
  profileImage: string | null;
  emailVerifiedAt: string | null;
  phone: string | null;
  /**
   * Set the moment Twilio Verify approves the consumer's phone code.
   * Paired with `profileImage` to decide whether the post-payment
   * "complete profile" gate needs to fire (see complete-profile-page).
   * Older servers may omit until the backend migration runs.
   */
  phoneVerifiedAt?: string | null;
  /** Present when API supports B2C company registration (older servers may omit). */
  consumerAccountKind?: ConsumerAccountKind | null;
  consumerCompanyRegistration?: ConsumerCompanyRegistrationSnapshot | null;
  /** True when the user has a password hash on file. Older servers may omit. */
  hasPassword?: boolean;
}

export function getSession(): Promise<SessionResponse> {
  return apiClient
    .get<SessionResponse>("/auth/session")
    .then((res) => res.data);
}

export interface TenantOption {
  id: string;
  name: string | null;
  role: string;
}

export function getMyTenants(): Promise<TenantOption[]> {
  return apiClient
    .get<{ tenants: TenantOption[] }>("/auth/me/tenants")
    .then((res) => res.data.tenants);
}

export function selectTenant(tenantId: string): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/select-tenant", { tenantId })
    .then((res) => res.data);
}

export function logout(params?: { sessionId?: string }): Promise<void> {
  return apiClient
    .post("/auth/logout", params?.sessionId != null ? { sessionId: params.sessionId } : {})
    .then(() => undefined)
    .finally(() => {
      clearAuth();
    });
}

export interface TenantCurrentResponse {
  id: string;
  name: string;
  legalName: string | null;
  rut: string;
  address: string | null;
  city: string | null;
  department: string | null;
  phone: string | null;
  contactEmail: string | null;
  activityStartDate: string | null;
  literalE: boolean | null;
  literalEVerified: boolean | null;
  language: string;
  industry: string | null;
  profession: string | null;
  companyType: CompanyTypeValue | null;
  profileImage: string | null;
  invoiceLogoImage: string | null;
  brandColor: string | null;
  createdAt: string;
  onboardingCompletedAt: string | null;
  fiscalProfileSkippedAt: string | null;
  /** Set when user acknowledged step 6 "go to Abitab" (Gestionalo path). Enables awaiting-Abitab screen. */
  step6AbitabAcknowledgedAt: string | null;
  hasDgiCredentials: boolean;
  planId: string | null;
  billingInterval: string | null;
  deletionRequestedAt: string | null;
  exoneratedRegimeEnabled: boolean | null;
  exoneratedRegimeLegend: string | null;
  exoneratedRegimeHeaderLegend: string | null;
  migratingFromAnotherPlatform: boolean | null;
  onboardingSituationChoice:
    | "scratch"
    | "issuer_need_regularization"
    | "already_issuer"
    | null;
  onboardingMode: "self" | "accountant" | null;
  hasDgiPortalUser: boolean;
  dgiPortalUserConfirmedAt: string | null;
  dgiPortalUserConfirmed: boolean;
}

export interface TenantPlanOption {
  id: string;
  nameKey: string;
  descriptionKey: string;
  priceMonthly: number;
  priceAnnual: number;
}

export function getTenantCurrent(): Promise<TenantCurrentResponse> {
  return apiClient
    .get<TenantCurrentResponse>("/tenants/current")
    .then((res) => res.data);
}

export function createDgiPortalUser(body: {
  documentType: "CI" | "NIE";
  documentNumber: string;
}): Promise<{ password: string }> {
  return apiClient
    .post<{ password: string }>("/tenants/current/dgi-portal-user", body)
    .then((res) => res.data);
}

export interface CurrentTenantRucInfoResponse {
  valid: boolean;
  legalName?: string;
  legalAddress?: string;
  city?: string;
  department?: string;
  phone?: string;
  email?: string;
  fechaInicio?: string;
}

export function getCurrentTenantRucInfo(): Promise<CurrentTenantRucInfoResponse> {
  return apiClient
    .get<CurrentTenantRucInfoResponse>("/tenants/current/ruc-info")
    .then((res) => res.data);
}

export function uploadCurrentTenantImage(
  file: File,
  target: "site" | "invoice" | "both" = "site"
): Promise<{ profileImage: string; invoiceLogoImage: string }> {
  const formData = new FormData();
  formData.append("image", file);
  formData.append("target", target);
  return apiClient
    .post<{ profileImage: string; invoiceLogoImage: string }>(
      "/tenants/current/upload-image",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    )
    .then((res) => res.data);
}

export function updateTenantBranding(
  brandColor: string | null
): Promise<{ brandColor: string | null }> {
  return apiClient
    .patch<{ brandColor: string | null }>("/tenants/current/branding", {
      brandColor,
    })
    .then((res) => res.data);
}

export interface UpdateTenantExoneratedRegimePayload {
  exoneratedRegimeEnabled?: boolean | null;
  exoneratedRegimeLegend?: string | null;
  exoneratedRegimeHeaderLegend?: string | null;
}

export function updateTenantExoneratedRegime(
  payload: UpdateTenantExoneratedRegimePayload
): Promise<{
  exoneratedRegimeEnabled: boolean | null;
  exoneratedRegimeLegend: string | null;
  exoneratedRegimeHeaderLegend: string | null;
}> {
  return apiClient
    .patch<{
      exoneratedRegimeEnabled: boolean | null;
      exoneratedRegimeLegend: string | null;
      exoneratedRegimeHeaderLegend: string | null;
    }>("/tenants/current/exonerated-regime", payload)
    .then((res) => res.data);
}

export function requestTenantDeletion(): Promise<void> {
  return apiClient
    .post("/tenants/current/request-deletion")
    .then(() => undefined);
}

export function sendOnboardingSupportRequest(
  type: "certificate_request" | "migration_help",
  options?: { notarialHandledByFiscalo?: boolean }
): Promise<void> {
  return apiClient
    .post("/tenants/current/onboarding-support-request", {
      type,
      ...(options?.notarialHandledByFiscalo === true && {
        notarialHandledByFiscalo: true,
      }),
    })
    .then(() => undefined);
}

export function sendFeedback(message: string): Promise<void> {
  return apiClient
    .post("/tenants/current/feedback", { message })
    .then(() => undefined);
}

export interface SupportMessageItem {
  id: string;
  direction: "support" | "tenant";
  body: string;
  authorUserId: string | null;
  authorDisplayName: string | null;
  createdAt: string;
  emailSentAt: string | null;
}

export function getSupportMessages(): Promise<SupportMessageItem[]> {
  return apiClient
    .get<SupportMessageItem[]>("/tenants/current/support-messages")
    .then((res) => res.data);
}

export function createSupportReply(
  message: string
): Promise<{ id: string; direction: string; body: string; createdAt: string }> {
  return apiClient
    .post<{ id: string; direction: string; body: string; createdAt: string }>(
      "/tenants/current/support-replies",
      { message: message.trim() }
    )
    .then((res) => res.data);
}

export function getTenantPlans(): Promise<TenantPlanOption[]> {
  return apiClient
    .get<TenantPlanOption[]>("/tenants/current/plans")
    .then((res) => res.data);
}

export function updateTenantPlan(
  planId: string,
  billingInterval: "monthly" | "annual"
): Promise<{ planId: string; billingInterval: string }> {
  return apiClient
    .patch<{ planId: string; billingInterval: string }>(
      "/tenants/current/plan",
      { planId, billingInterval }
    )
    .then((res) => res.data);
}

export function skipFiscalProfile(): Promise<void> {
  return apiClient
    .post("/tenants/current/skip-fiscal-profile")
    .then(() => undefined);
}

export function completeOnboarding(): Promise<void> {
  return apiClient
    .post("/tenants/current/complete-onboarding")
    .then(() => undefined);
}

/** Notifies tenant owner/admin that the accountant finished their onboarding steps. Call when accountant clicks "Ir al panel". */
export function notifyOwnerAccountantCompleted(): Promise<void> {
  return apiClient
    .post("/tenants/current/notify-owner-accountant-completed")
    .then(() => undefined);
}

export interface CurrentTenantDgiCredentialsStatus {
  hasCredentials: boolean;
  certificateValidFrom?: string;
  certificateValidTo?: string;
  certificateSubject?: string;
  certificateIssuer?: string;
  idEmisor?: number;
  configurationError?: boolean;
  lastConfigurationError?: string | null;
}

export function getCurrentTenantDgiCredentialsStatus(): Promise<CurrentTenantDgiCredentialsStatus> {
  return apiClient
    .get<CurrentTenantDgiCredentialsStatus>("/tenants/current/dgi-credentials/status")
    .then((res) => res.data);
}

/** File input accept value for DGI certificate uploads (PFX only). */
export const DGI_CERTIFICATE_FILE_ACCEPT = ".pfx";

/**
 * Derives idEmisor (numeric) from a tenant/company RUT string.
 * Normalizes to digits only, pads to 12 chars, and parses. Use for DGI credential uploads.
 */
export function rutToIdEmisor(rut: string): number {
  const digits = rut.replace(/\D/g, "").padStart(12, "0").slice(0, 12);
  return parseInt(digits, 10);
}

export function uploadCurrentTenantDgiCredentials(
  file: File,
  idEmisor: number,
  passphrase?: string
): Promise<void> {
  const formData = new FormData();
  formData.append("p12", file);
  formData.append("idEmisor", String(idEmisor));
  if (passphrase != null && passphrase !== "") {
    formData.append("passphrase", passphrase);
  }
  return apiClient
    .post("/tenants/current/dgi-credentials", formData)
    .then(() => undefined);
}

export interface TenantDocument {
  id: string;
  name: string;
  fileUrl: string;
  contentType: string;
  originalFilename: string;
  createdAt: string;
}

export function getTenantDocuments(): Promise<TenantDocument[]> {
  return apiClient
    .get<TenantDocument[]>("/tenants/current/documents")
    .then((res) => res.data);
}

export function uploadTenantDocument(
  file: File,
  name?: string
): Promise<TenantDocument> {
  const formData = new FormData();
  formData.append("file", file);
  if (name != null && name.trim() !== "") {
    formData.append("name", name.trim());
  }
  return apiClient
    .post<TenantDocument>("/tenants/current/documents", formData)
    .then((res) => res.data);
}

export function updateTenantDocumentName(
  id: string,
  name: string
): Promise<TenantDocument> {
  return apiClient
    .patch<TenantDocument>(`/tenants/current/documents/${id}`, { name: name.trim() })
    .then((res) => res.data);
}

export function deleteTenantDocument(id: string): Promise<void> {
  return apiClient
    .delete(`/tenants/current/documents/${id}`)
    .then(() => undefined);
}

export interface UpdateTenantParams {
  /** Public / commercial name (shown on invoices). */
  name?: string | null;
  legalName?: string | null;
  address?: string | null;
  city?: string | null;
  department?: string | null;
  phone?: string | null;
  contactEmail?: string | null;
  activityStartDate?: string | null;
  literalE?: boolean | null;
  language?: string;
  industry?: string | null;
  profession?: string | null;
  migratingFromAnotherPlatform?: boolean | null;
  onboardingSituationChoice?:
    | "scratch"
    | "issuer_need_regularization"
    | "already_issuer"
    | null;
  onboardingMode?: "self" | "accountant" | null;
  /** ISO timestamp when user acknowledged step 6 "go to Abitab" (Gestionalo path). */
  step6AbitabAcknowledgedAt?: string | null;
}

export interface UpdateTenantResponse {
  tenantId: string;
  tenantName: string | null;
  language: string;
  industry: string | null;
  profession: string | null;
}

export function updateTenant(
  params: UpdateTenantParams
): Promise<UpdateTenantResponse> {
  return apiClient
    .patch<UpdateTenantResponse>("/auth/tenant", params)
    .then((res) => res.data);
}

export interface ImpersonateResponse {
  accessToken: string;
  sessionId: string;
  expiresAt: string;
}

export function impersonate(tenantId: string): Promise<ImpersonateResponse> {
  return apiClient
    .post<ImpersonateResponse>("/auth/impersonate", { tenantId })
    .then((res) => res.data);
}

export function endImpersonation(): Promise<ImpersonateResponse> {
  return apiClient
    .post<ImpersonateResponse>("/auth/end-impersonation")
    .then((res) => res.data);
}

export function applyVerifyResponse(response: VerifyResponse): void {
  setToken(response.accessToken);
  if ("refreshToken" in response && typeof response.refreshToken === "string") {
    setRefreshToken(response.refreshToken);
  }
  if ("accessTokenExpiresAt" in response && typeof response.accessTokenExpiresAt === "string") {
    setAccessExpiresAt(response.accessTokenExpiresAt);
  }
  setStoredUser({
    id: response.user.id,
    email: response.user.email,
    displayName: response.user.displayName ?? null,
    firstName: response.user.firstName ?? null,
    lastName: response.user.lastName ?? null,
    profileImage: response.user.profileImage ?? null,
  });
}

export function applyImpersonateResponse(response: ImpersonateResponse): void {
  setToken(response.accessToken);
}

export function hasStoredToken(): boolean {
  return getToken() != null;
}

export interface TenantInvitationInfo {
  email: string;
  role: string;
  tenantId: string;
  tenantName: string;
  /** Tenant preferred language (e.g. 'es', 'en') for accept-invitation page. */
  language?: string;
  /** Tenant profile image URL for branding; when set, show in accept-invitation header. */
  tenantProfileImageUrl?: string | null;
  /** Tenant legal name; when no profile image, show in accept-invitation header. */
  tenantLegalName?: string;
  /** True when the invited email already has an account; use accept-existing flow. */
  userAlreadyExists?: boolean;
}

export function getTenantInvitationByToken(token: string): Promise<TenantInvitationInfo> {
  return apiClient
    .get<TenantInvitationInfo>(`/auth/invitations/${encodeURIComponent(token)}`)
    .then((res) => res.data);
}

export interface AcceptTenantInvitationParams {
  firstName: string;
  lastName: string;
  password: string;
}

export function acceptTenantInvitation(
  token: string,
  params: AcceptTenantInvitationParams
): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>(`/auth/invitations/${encodeURIComponent(token)}/accept`, {
      firstName: params.firstName.trim(),
      lastName: params.lastName.trim(),
      password: params.password,
    })
    .then((res) => res.data);
}

export function acceptTenantInvitationExistingUser(
  token: string
): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>(
      `/auth/invitations/${encodeURIComponent(token)}/accept-existing`
    )
    .then((res) => res.data);
}

export interface UpdateProfileParams {
  firstName?: string;
  lastName?: string;
}

export function updateProfile(params: UpdateProfileParams): Promise<{
  firstName: string | null;
  lastName: string | null;
}> {
  return apiClient
    .patch<{ firstName: string | null; lastName: string | null }>(
      "/auth/me",
      params
    )
    .then((res) => res.data);
}

export interface UpdateLegalDataParams {
  firstName: string;
  secondName?: string | null;
  firstLastName: string;
  secondLastName?: string | null;
  documentType: "national_id" | "passport";
  documentNumber: string;
}

export function updateLegalData(
  params: UpdateLegalDataParams
): Promise<{ ok: true }> {
  return apiClient
    .patch<{ ok: true }>("/auth/me/legal-data", {
      firstName: params.firstName.trim(),
      secondName: params.secondName ?? null,
      firstLastName: params.firstLastName.trim(),
      secondLastName: params.secondLastName ?? null,
      documentType: params.documentType,
      documentNumber: params.documentNumber.trim(),
    })
    .then((res) => res.data);
}

export interface DgiPersonNameResponse {
  primerNombre: string | null;
  segundoNombre: string | null;
  primerApellido: string | null;
  segundoApellido: string | null;
}

export function getDgiPersonName(ci: string): Promise<DgiPersonNameResponse> {
  const trimmed = ci.replace(/\D/g, "").trim();
  return apiClient
    .get<DgiPersonNameResponse>("/auth/dgi/person-name", {
      params: { ci: trimmed },
    })
    .then((res) => res.data);
}

export interface CreateDiditSessionParams {
  language?: string;
}

export interface CreateDiditSessionResponse {
  verification_url: string;
  session_id?: string;
}

export function uploadProfileImage(file: File): Promise<{ profileImage: string }> {
  const formData = new FormData();
  formData.append("image", file);
  return apiClient
    .post<{ profileImage: string }>("/auth/me/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export function sendMyVerificationEmail(locale?: string): Promise<void> {
  return apiClient
    .post("/auth/me/send-verification-email", locale != null ? { locale } : {})
    .then(() => undefined);
}

export function verifyMyEmail(code: string): Promise<void> {
  return apiClient
    .post("/auth/me/verify-email", { code: code.trim() })
    .then(() => undefined);
}

export interface NotaryInvitationInfo {
  email: string;
  valid: boolean;
  expiresAt: string;
}

export interface AccountantInvitationInfo {
  email: string;
  valid: boolean;
  expiresAt: string;
  manualApprovalStatus: "pending" | "approved" | "declined" | null;
}

export type AccountantInviteVerificationStatusPayload = {
  certificateUploaded: boolean;
  /** Optional on the wire: an older server omits it, and absent means not declared. */
  fiscalDeclared?: boolean;
  manualApprovalStatus: "pending" | "approved" | "declined" | null;
};

export function getNotaryInvitationByToken(
  token: string
): Promise<NotaryInvitationInfo> {
  return apiClient
    .get<NotaryInvitationInfo>(
      `/auth/notary-invitations/${encodeURIComponent(token)}`
    )
    .then((res) => res.data);
}

export function requestNotaryInvitePhoneCode(
  token: string,
  phone: string,
  channel: "sms" | "call" = "sms"
): Promise<{ ok: true; alreadyVerified: boolean }> {
  return apiClient
    .post<{ ok: true; alreadyVerified: boolean }>(
      `/auth/notary-invitations/${encodeURIComponent(token)}/request-phone-code`,
      { phone: phone.trim(), channel }
    )
    .then((res) => res.data);
}

export function verifyNotaryInvitePhoneCode(
  token: string,
  phone: string,
  code: string
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(
      `/auth/notary-invitations/${encodeURIComponent(token)}/verify-phone-code`,
      { phone: phone.trim(), code: code.trim() }
    )
    .then((res) => res.data);
}

/**
 * Send a Twilio Verify code (SMS or voice) to the authenticated user's
 * phone. Powers the post-payment "complete profile" gate (see
 * complete-profile-page). Returns `alreadyVerified: true` when the same
 * number is already approved server-side, so the UI can skip the code
 * step and forward directly.
 */
export function requestMyPhoneCode(
  phone: string,
  channel: "sms" | "call" = "sms"
): Promise<{ ok: true; alreadyVerified: boolean }> {
  return apiClient
    .post<{ ok: true; alreadyVerified: boolean }>(
      `/auth/me/phone/request-code`,
      { phone: phone.trim(), channel }
    )
    .then((res) => res.data);
}

/**
 * Submit the 6-digit code the user received. On approval the backend
 * writes user.phone + user.phoneVerifiedAt AND auto-promotes any of the
 * user's `pending_profile_completion` requests to `pending`. Caller
 * should refresh the session afterwards so the new phoneVerifiedAt
 * propagates through AuthContext.
 */
export function verifyMyPhoneCode(
  phone: string,
  code: string
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(
      `/auth/me/phone/verify-code`,
      { phone: phone.trim(), code: code.trim() }
    )
    .then((res) => res.data);
}

/**
 * THE PROFESSION-AGNOSTIC INVITE CLIENT (spec 762f22ba, slice A).
 *
 * Every function below talks to `professional-invitations/*`, where the
 * profession is resolved from the TOKEN rather than named in the path. The
 * profession-named functions above still exist and still work — invitations
 * already in flight carry links to those routes — but nothing new should be
 * written against them, because a profession the registry knows about has no
 * equivalent there.
 */
export type ProfessionalInvitationPayload = {
  email: string;
  valid: boolean;
  expiresAt: string;
  professionSlug: string | null;
  displayName: string | null;
  /** How this profession's credential is checked. The FIRST of the set below. */
  verificationStrategy: string | null;
  /**
   * EVERY verification this profession requires, in order (spec 43ed8ad8).
   *
   * The acceptance wizard is composed from these, so a profession requiring
   * two is asked for both strategies' inputs in one flow. The scalar above is
   * kept only so a portal bundle already open in a browser keeps routing; it
   * is the first element of this set.
   *
   * SERVED ALL ALONG AND UNDECLARED UNTIL NOW. getProfessionalInviteByToken
   * has returned it since the server learned to fold over a set, and the
   * controller's narrower return type never listed it — as it never listed
   * availableDepartments either, which the coverage step has read at runtime
   * the whole time. Declaring it is what lets a client ask for it without
   * casting.
   */
  verificationStrategies?: readonly string[];
  /**
   * The i18n namespace that NAMES this profession (spec 798af82c).
   *
   * An acceptance page is chosen by verificationStrategy, so the page a
   * profession lands on is named after whichever profession the flow was
   * written for — a traductor on the register-lookup page was called an
   * escribano eight times. This is what lets the page use the profession's own
   * word, resolved against the portal's own bundle because the portal owns
   * locale on this route.
   */
  copyNamespace?: string | null;
  /*
    THE DEPARTMENT VOCABULARY, served with the invitation. An acceptance page
    has no session, so it cannot call the authenticated coverage endpoint, and
    professionalCoverageService is explicit that the list must not be
    hardcoded: "The server owns this list; never hardcode a copy."
  */
  availableDepartments?: readonly string[];
};

export function getProfessionalInvitationByToken(
  token: string
): Promise<ProfessionalInvitationPayload> {
  return apiClient
    .get<ProfessionalInvitationPayload>(
      `/auth/professional-invitations/${encodeURIComponent(token)}`
    )
    .then((res) => res.data);
}

export function getProfessionalInviteStatus(token: string): Promise<{
  certificateUploaded: boolean;
  fiscalDeclared: boolean;
  manualApprovalStatus: "pending" | "approved" | "declined" | null;
}> {
  return apiClient
    .get<{
      certificateUploaded: boolean;
      fiscalDeclared: boolean;
      manualApprovalStatus: "pending" | "approved" | "declined" | null;
    }>(`/auth/professional-invitations/${encodeURIComponent(token)}/verification-status`)
    .then((res) => res.data);
}

export function validateProfessionalInvite(params: {
  token: string;
  cedula: string;
  /** The profession's credential, where its strategy verifies one. */
  credential?: string;
  manualFirstName?: string;
  manualSecondFirstName?: string;
  manualFirstLastName?: string;
  manualSecondLastName?: string;
}): Promise<{ valid: true; firstName: string; lastName: string }> {
  // FORWARDED WHOLE, deliberately — see accept-wrapper-forwards-every-field:
  // rebuilding a body field by field is how coverageAreas reached the server
  // as undefined on one of the two paths.
  return apiClient
    .post<{ valid: true; firstName: string; lastName: string }>(
      "/auth/professional-invitations/validate",
      params
    )
    .then((res) => res.data);
}

export function lookupProfessionalInvitePersonByDocument(
  token: string,
  cedula: string
): Promise<{
  primerNombre: string;
  segundoNombre: string | null;
  primerApellido: string;
  segundoApellido: string | null;
}> {
  return apiClient
    .post<{
      primerNombre: string;
      segundoNombre: string | null;
      primerApellido: string;
      segundoApellido: string | null;
    }>("/auth/professional-invitations/person-by-document", { token, cedula })
    .then((res) => res.data);
}

export function requestProfessionalInvitePhoneCode(
  token: string,
  phone: string,
  channel: "sms" | "call" = "sms"
): Promise<{ ok: true; alreadyVerified: boolean }> {
  return apiClient
    .post<{ ok: true; alreadyVerified: boolean }>(
      `/auth/professional-invitations/${encodeURIComponent(token)}/request-phone-code`,
      { phone, channel }
    )
    .then((res) => res.data);
}

export function verifyProfessionalInvitePhoneCode(
  token: string,
  phone: string,
  code: string
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(
      `/auth/professional-invitations/${encodeURIComponent(token)}/verify-phone-code`,
      { phone, code }
    )
    .then((res) => res.data);
}

export function recordProfessionalInviteFiscalDeclaration(
  token: string,
  declaration: {
    rut?: string;
    razonSocial?: string;
    issuesCfe?: boolean;
    cfeProvider?: string | null;
  }
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(
      `/auth/professional-invitations/${encodeURIComponent(token)}/fiscal-declaration`,
      declaration
    )
    .then((res) => res.data);
}

export function uploadProfessionalInviteCertificate(
  token: string,
  file: File
): Promise<{ ok: true }> {
  const body = new FormData();
  body.append("certificate", file);
  return apiClient
    .post<{ ok: true }>(
      `/auth/professional-invitations/${encodeURIComponent(token)}/professional-certificate`,
      body
    )
    .then((res) => res.data);
}

/**
 * Accepting an invitation, for ANY profession in the registry.
 *
 * MULTIPART, because it carries the profile image — which is why `issuesCfe`
 * and `coverageAreas` go over the wire as text here where the accountant's
 * JSON route had a boolean and an array. The controller coerces them back.
 *
 * NO `matricula`: the credential is whatever the profession's verification
 * strategy asks for, so the field is named for its ROLE and is optional —
 * whether one is required is the strategy's refusal to make, not this
 * client's (decision a78bb4f).
 */
export interface AcceptProfessionalInviteParams {
  cedula: string;
  /** The profession's own credential, where its strategy verifies one. */
  credential?: string;
  firstName: string;
  lastName: string;
  password: string;
  phone: string;
  /**
   * Sent on THIS request rather than through a second upload call, because
   * this route is multipart and can carry it — so the account is created with
   * its avatar already in place.
   */
  profileImageFile?: File;
  /** The fiscal declaration — written as the user row is created. */
  rut?: string;
  razonSocial?: string;
  issuesCfe?: boolean;
  /** Departments covered; asked at acceptance (task 4228162). */
  coverageAreas?: string[];
}

export function acceptProfessionalInvite(
  token: string,
  params: AcceptProfessionalInviteParams
): Promise<VerifyResponse> {
  const body = new FormData();
  body.append("token", token);
  body.append("cedula", params.cedula.replace(/\D/g, ""));
  body.append("firstName", params.firstName.trim());
  body.append("lastName", params.lastName.trim());
  body.append("password", params.password);
  body.append("phone", params.phone.trim());
  /*
    APPENDED ONLY WHEN PRESENT, every one of them. FormData has no undefined:
    appending one sends the literal string "undefined", which satisfies the
    backend's optional check and then fails its own pattern — and absence is
    what the backend reads as "never declared". The same reasoning as
    verifyNotaryInvite, which this route generalises.
  */
  if (params.profileImageFile !== undefined) {
    body.append("profileImage", params.profileImageFile);
  }
  if (params.credential !== undefined && params.credential !== "") {
    body.append("credential", params.credential.replace(/\s/g, ""));
  }
  if (params.rut !== undefined) {
    body.append("rut", params.rut.replace(/\D/g, ""));
  }
  if (params.razonSocial !== undefined) {
    body.append("razonSocial", params.razonSocial.trim());
  }
  if (params.issuesCfe !== undefined) {
    body.append("issuesCfe", params.issuesCfe ? "true" : "false");
  }
  /*
    COMMA-SEPARATED and only when non-empty. An empty selection means "covers
    nowhere in particular", which the backend already represents as no rows;
    sending "" would have the controller split it into [""] and the service
    reject "" as an unknown department.
  */
  if (params.coverageAreas !== undefined && params.coverageAreas.length > 0) {
    body.append("coverageAreas", params.coverageAreas.join(","));
  }
  return apiClient
    .post<VerifyResponse>("/auth/professional-invitations/accept", body, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

/**
 * Whether a human is reviewing this invitation.
 *
 * It used to report identity verification too. That is gone with the KYC
 * provider (task 9edda72), which had been disabled in production behind a
 * flag that stamped the column rather than skipping the step — so the field
 * answered "verified" for everyone and gated nothing.
 */
export function getNotaryInviteVerificationStatus(
  token: string
): Promise<{ manualApprovalStatus?: "pending" | "approved" | "declined" | null }> {
  const url = `/auth/notary-invitations/${encodeURIComponent(token)}/verification-status`;
  return apiClient
    .get<{ manualApprovalStatus?: "pending" | "approved" | "declined" | null }>(url)
    .then((res) => res.data);
}

export interface ValidateNotaryInviteResult {
  valid: true;
  firstName: string;
  lastName: string;
}

export function validateNotaryInvite(
  token: string,
  cedula: string,
  matricula: string
): Promise<ValidateNotaryInviteResult> {
  return apiClient
    .post<ValidateNotaryInviteResult>("/auth/validate-notary-invite", {
      token,
      cedula: cedula.replace(/\D/g, ""),
      matricula: matricula.replace(/\D/g, ""),
    })
    .then((res) => res.data);
}

export interface VerifyNotaryInviteParams {
  cedula: string;
  matricula: string;
  firstName: string;
  lastName: string;
  password: string;
  profileImageFile: File;
  phone: string;
  /**
   * The fiscal declaration, sent on THIS call rather than through
   * recordNotaryFiscalDeclaration.
   *
   * The notary's user row is created by this request, and the fiscal columns
   * live on that row — so a separate declaration call earlier in the flow has
   * no row to write to, and later has no page to run on, since this logs them
   * in and the page navigates to /requests. Optional in the type because the
   * backend accepts their absence; the form requires them.
   */
  rut?: string;
  razonSocial?: string;
  issuesCfe?: boolean;
  /**
   * The departments this professional covers.
   *
   * Sent on THIS call for the same reason the fiscal fields are: the user row
   * is created by this request, and coverage hangs off that row. Optional
   * because an empty selection is a legitimate answer, not an unfinished form.
   */
  coverageAreas?: string[];
}

export function verifyNotaryInvite(
  token: string,
  params: VerifyNotaryInviteParams
): Promise<VerifyResponse> {
  const formData = new FormData();
  formData.append("token", token);
  formData.append("cedula", params.cedula.replace(/\D/g, ""));
  formData.append("matricula", params.matricula.replace(/\D/g, ""));
  formData.append("firstName", params.firstName.trim());
  formData.append("lastName", params.lastName.trim());
  formData.append("password", params.password);
  formData.append("phone", params.phone.trim());
  formData.append("profileImage", params.profileImageFile);
  // APPENDED ONLY WHEN PRESENT. FormData has no undefined — appending one
  // sends the literal string "undefined", which would pass the backend's
  // optional check and then fail its 12-digit RUT pattern. Omitting the key
  // entirely is what makes the field genuinely absent, and absence is what the
  // backend treats as "never declared".
  if (params.rut !== undefined) {
    formData.append("rut", params.rut.replace(/\D/g, ""));
  }
  if (params.razonSocial !== undefined) {
    formData.append("razonSocial", params.razonSocial.trim());
  }
  if (params.issuesCfe !== undefined) {
    formData.append("issuesCfe", params.issuesCfe ? "true" : "false");
  }
  /*
    COMMA-SEPARATED, because this endpoint is multipart and FormData carries
    only text — the same reason issuesCfe is a string above. The controller
    splits it and the service takes a real array.

    APPENDED ONLY WHEN NON-EMPTY, for the reason spelled out above: an empty
    selection means "covers nowhere in particular", which the backend already
    represents as no rows. Sending an empty string would have the controller
    split it into [""] and the service reject "" as an unknown department.
  */
  if (params.coverageAreas !== undefined && params.coverageAreas.length > 0) {
    formData.append("coverageAreas", params.coverageAreas.join(","));
  }
  return apiClient
    .post<VerifyResponse>("/auth/verify-notary-invite", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
}

export function getAccountantInvitationByToken(
  token: string
): Promise<AccountantInvitationInfo> {
  return apiClient
    .get<AccountantInvitationInfo>(
      `/auth/accountant-invitations/${encodeURIComponent(token)}`
    )
    .then((res) => res.data);
}

export function requestAccountantInvitePhoneCode(
  token: string,
  phone: string,
  channel: "sms" | "call" = "sms"
): Promise<{ ok: true; alreadyVerified: boolean }> {
  return apiClient
    .post<{ ok: true; alreadyVerified: boolean }>(
      `/auth/accountant-invitations/${encodeURIComponent(token)}/request-phone-code`,
      { phone: phone.trim(), channel }
    )
    .then((res) => res.data);
}

export function verifyAccountantInvitePhoneCode(
  token: string,
  phone: string,
  code: string
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(
      `/auth/accountant-invitations/${encodeURIComponent(token)}/verify-phone-code`,
      { phone: phone.trim(), code: code.trim() }
    )
    .then((res) => res.data);
}

export function getAccountantInviteVerificationStatus(
  token: string
): Promise<AccountantInviteVerificationStatusPayload> {
  const url = `/auth/accountant-invitations/${encodeURIComponent(token)}/verification-status`;
  return apiClient
    .get<AccountantInviteVerificationStatusPayload>(url)
    .then((res) => res.data);
}

/**
 * Record the professional's fiscal declaration during onboarding (spec af19d370).
 *
 * Token-addressed like the certificate upload beside it, because this is
 * answered before the professional has a session.
 */
export type ProfessionalFiscalDeclaration = {
  rut: string;
  issuesCfe: boolean;
  /**
   * The registered name behind the RUT. REQUIRED by the backend DTO — it is
   * what the platform's own invoice to the professional is addressed to, and
   * it is not their display name.
   */
  razonSocial: string;
  cfeProvider?: string;
};

export function recordAccountantFiscalDeclaration(
  token: string,
  declaration: ProfessionalFiscalDeclaration
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(
      `/auth/accountant-invitations/${encodeURIComponent(token)}/fiscal-declaration`,
      declaration
    )
    .then((res) => res.data);
}

/**
 * The notary half, which had no client at all until 2026-10-08.
 *
 * The endpoint has existed since the declaration shipped (auth.controller.ts
 * routes both professions to the same service), but only the accountant
 * invitation page ever called it — so notaries were never asked for a RUT and
 * every notary row still reads as undeclared. That absence is why the backfill
 * population is a missing form rather than only missing data.
 */
export function recordNotaryFiscalDeclaration(
  token: string,
  declaration: ProfessionalFiscalDeclaration
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(
      `/auth/notary-invitations/${encodeURIComponent(token)}/fiscal-declaration`,
      declaration
    )
    .then((res) => res.data);
}

export function uploadAccountantInviteProfessionalCertificate(
  token: string,
  certificateFile: File
): Promise<{ ok: true }> {
  const formData = new FormData();
  formData.append("certificate", certificateFile);
  return apiClient
    .post<{ ok: true }>(
      `/auth/accountant-invitations/${encodeURIComponent(token)}/professional-certificate`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    )
    .then((res) => res.data);
}

export interface ValidateAccountantInviteResult {
  valid: true;
  firstName: string;
  lastName: string;
}

export interface ValidateAccountantInviteManualNames {
  manualFirstName?: string;
  manualSecondFirstName?: string;
  manualFirstLastName?: string;
  manualSecondLastName?: string;
}

export function validateAccountantInvite(
  token: string,
  cedula: string,
  manualNames?: ValidateAccountantInviteManualNames
): Promise<ValidateAccountantInviteResult> {
  return apiClient
    .post<ValidateAccountantInviteResult>("/auth/validate-accountant-invite", {
      token,
      cedula: cedula.replace(/\D/g, ""),
      ...(manualNames ?? {}),
    })
    .then((res) => res.data);
}

export interface AccountantInvitePersonByDocumentResult {
  primerNombre: string;
  segundoNombre: string | null;
  primerApellido: string;
  segundoApellido: string | null;
}

/** Unauthenticated: valid invite token + cédula. Names from identity registry (DGI GetNombre). */
export function lookupAccountantInvitePersonByDocument(
  token: string,
  cedula: string
): Promise<AccountantInvitePersonByDocumentResult> {
  return apiClient
    .post<AccountantInvitePersonByDocumentResult>(
      "/auth/accountant-invite/person-by-document",
      {
        token,
        cedula: cedula.replace(/\D/g, ""),
      }
    )
    .then((res) => res.data);
}

/** Body for POST /auth/verify-accountant-invite (account is created first; avatar via POST /auth/me/avatar). */
export interface VerifyAccountantInviteBody {
  cedula: string;
  firstName: string;
  lastName: string;
  password: string;
  phone: string;
  /**
   * The fiscal declaration, sent on THIS call rather than through
   * recordAccountantFiscalDeclaration.
   *
   * The fiscal columns live on the user row, and this request is what creates
   * it. Posting them from the onboarding page's step 4 — before this ran —
   * 404'd on a row that did not exist, and the page then routed back to step 4
   * forever because the declaration never landed.
   */
  rut?: string;
  razonSocial?: string;
  issuesCfe?: boolean;
  /**
   * Departments covered.
   *
   * Sent on THIS call for the same reason the fiscal fields are: the user row
   * is created by this request and coverage hangs off it. A real array, since
   * this route is JSON rather than multipart. Optional because an empty
   * selection is a legitimate answer, not an unfinished form.
   */
  coverageAreas?: string[];
}

export function verifyAccountantInvite(
  token: string,
  params: VerifyAccountantInviteBody
): Promise<VerifyResponse> {
  return apiClient
    .post<VerifyResponse>("/auth/verify-accountant-invite", {
      token,
      cedula: params.cedula.replace(/\D/g, ""),
      firstName: params.firstName.trim(),
      lastName: params.lastName.trim(),
      password: params.password,
      phone: params.phone.trim(),
      // Omitted entirely when absent rather than sent as undefined, so the
      // backend can tell "not declared" from a declared value.
      ...(params.rut !== undefined ? { rut: params.rut.replace(/\D/g, "") } : {}),
      ...(params.razonSocial !== undefined
        ? { razonSocial: params.razonSocial.trim() }
        : {}),
      ...(params.issuesCfe !== undefined ? { issuesCfe: params.issuesCfe } : {}),
      // Same omit-when-absent rule. An empty selection is sent as nothing at
      // all rather than as [], because "covers nowhere in particular" is
      // already represented by having no rows.
      ...(params.coverageAreas !== undefined && params.coverageAreas.length > 0
        ? { coverageAreas: params.coverageAreas }
        : {}),
    })
    .then((res) => res.data);
}

// Re-exported so existing call sites keep importing from authService while the
// logic itself stays loadable without the HTTP client.
export {
  isTotpRequiredResponse,
  requiresTotpEnrollment,
  type ProfessionalTotpRequiredResponse,
};
